import { useCallback, useEffect, useRef } from "react";

import type { ActiveLogView } from "@/atoms/ui-state";
import type { EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import type { TabName } from "@/components/environments/SuperOnaConversationHeader";
import type { ChangesScope } from "@/routes/environments/code-changes/diff-mode";
import type { DrawerTab } from "@/routes/environments/details/SuperOnaEnvironmentBottomDrawer";
import type { RightPanelTab } from "@/routes/environments/details/SuperOnaRightPanelHeader";

/**
 * State for a single split pane — which tabs it contains and which is active.
 */
export type SplitPaneState = {
    tabs: TabName[];
    activeTab: TabName;
};

/**
 * When non-null, the content area is split into two panes below the
 * main header, each with its own tab bar and active tab.
 */
export type SplitState = {
    left: SplitPaneState;
    right: SplitPaneState;
};

/**
 * Shape of the per-environment tab state persisted to sessionStorage.
 */
export type PersistedTabState = {
    activeTab: TabName;
    selectedFilePath: string | null;
    isEnvironmentTabOpen: boolean;
    activeLogView: ActiveLogView | null;
    viewMode: EnvironmentViewMode;
    /** Active tab in the right sidebar (Changes / All Files / Comments). */
    rightPanelTab: RightPanelTab;
    /** Sub-selector within the Changes tab (Uncommitted / All changes). */
    changesScope: ChangesScope;
    /** Active tab in the bottom drawer (Ports & Services / Tasks / Terminal). */
    drawerTab: DrawerTab;
    /** ID of the selected agent execution (conversation tab). */
    selectedExecutionId: string | null;
    /** When set, the content area is split into two panes below the header. */
    splitState: SplitState | null;
};

const STORAGE_KEY_PREFIX = "env-tabs:";

function storageKey(environmentId: string): string {
    return `${STORAGE_KEY_PREFIX}${environmentId}`;
}

/**
 * Read persisted tab state for an environment from sessionStorage.
 * Returns null if nothing is stored or the stored value is invalid.
 * Missing sidebar fields are filled with defaults for backward compatibility.
 */
export function readEnvironmentTabState(environmentId: string): PersistedTabState | null {
    try {
        const raw = sessionStorage.getItem(storageKey(environmentId));
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!isPersistedTabState(parsed)) return null;
        // Fill defaults for sidebar fields added after the initial schema.
        return { ...DEFAULT_STATE, ...parsed };
    } catch {
        return null;
    }
}

/**
 * Write persisted tab state for an environment to sessionStorage.
 */
export function writeEnvironmentTabState(environmentId: string, state: PersistedTabState): void {
    try {
        sessionStorage.setItem(storageKey(environmentId), JSON.stringify(state));
    } catch {
        // sessionStorage may be full or unavailable — silently ignore.
    }
}

const DEFAULT_STATE: PersistedTabState = {
    activeTab: "conversation",
    selectedFilePath: null,
    isEnvironmentTabOpen: false,
    activeLogView: null,
    viewMode: "details",
    rightPanelTab: "changes",
    changesScope: "uncommitted",
    drawerTab: "ports-services",
    selectedExecutionId: null,
    splitState: null,
};

/**
 * Merge partial updates into the persisted state for an environment.
 * Reads the current stored state, applies the partial update, and writes back.
 * If no state exists yet, uses defaults for missing fields.
 */
export function mergeEnvironmentTabState(environmentId: string, partial: Partial<PersistedTabState>): void {
    const current = readEnvironmentTabState(environmentId) ?? DEFAULT_STATE;
    writeEnvironmentTabState(environmentId, { ...current, ...partial });
}

/**
 * Validate a restored activeTab against the state that's actually available.
 *
 * Dynamic tabs ("environment", "file", "logs", "browser") are only rendered
 * when their prerequisite state is present. If the persisted tab refers to a
 * dynamic tab whose prerequisite isn't met, this returns "conversation" so the
 * user never sees a blank screen.
 *
 * "browser" is always considered valid here because port availability is
 * determined asynchronously — a runtime effect handles that case.
 */
export function resolveActiveTab(
    tab: TabName,
    context: {
        isEnvironmentTabOpen: boolean;
        selectedFilePath: string | null;
        hasActiveLogView: boolean;
    },
): TabName {
    switch (tab) {
        case "code":
        case "conversation":
        case "browser":
            return tab;
        case "environment":
            return context.isEnvironmentTabOpen ? tab : "conversation";
        case "file":
            return context.selectedFilePath ? tab : "conversation";
        case "logs":
            return context.hasActiveLogView ? tab : "conversation";
        default:
            // Unknown dynamic tab (e.g. claude-agent-*, codex-agent-*) — these
            // are never persisted (rejected by isPersistedTabState) but handle
            // gracefully just in case.
            return "conversation";
    }
}

const VALID_TAB_NAMES = new Set<string>(["code", "conversation", "file", "environment", "logs", "browser"]);
const VALID_VIEW_MODES = new Set<string>(["details", "vscode", "changes"]);
const VALID_RIGHT_PANEL_TABS = new Set<string>(["changes", "all-files", "comments"]);
const VALID_CHANGES_SCOPES = new Set<string>(["uncommitted", "all"]);
const VALID_DRAWER_TABS = new Set<string>(["ports-services", "tasks"]);

function isPersistedTabState(value: unknown): value is PersistedTabState {
    if (typeof value !== "object" || value === null) return false;
    const obj = value as Record<string, unknown>;

    if (!VALID_TAB_NAMES.has(obj.activeTab as string)) return false;
    if (typeof obj.selectedFilePath !== "string" && obj.selectedFilePath !== null) return false;
    if (typeof obj.isEnvironmentTabOpen !== "boolean") return false;
    if (!VALID_VIEW_MODES.has(obj.viewMode as string)) return false;

    // activeLogView is either null or a typed discriminated union
    if (obj.activeLogView !== null) {
        if (typeof obj.activeLogView !== "object") return false;
        const logView = obj.activeLogView as Record<string, unknown>;
        if (logView.type === "service") {
            if (typeof logView.serviceId !== "string") return false;
            if (typeof logView.environmentId !== "string") return false;
        } else if (logView.type === "task-run") {
            if (typeof logView.taskId !== "string") return false;
            if (typeof logView.runId !== "string") return false;
            if (typeof logView.environmentId !== "string") return false;
        } else {
            return false;
        }
    }

    // Sidebar tab fields — optional for backward compatibility with older
    // persisted data. When missing, mergeEnvironmentTabState fills defaults.
    if ("rightPanelTab" in obj && !VALID_RIGHT_PANEL_TABS.has(obj.rightPanelTab as string)) return false;
    if ("changesScope" in obj && !VALID_CHANGES_SCOPES.has(obj.changesScope as string)) return false;
    if ("drawerTab" in obj) {
        const dt = obj.drawerTab as string;
        // Accept static tabs and dynamic terminal-{id} tabs
        if (!VALID_DRAWER_TABS.has(dt) && !dt.startsWith("terminal-")) return false;
    }

    if ("selectedExecutionId" in obj && typeof obj.selectedExecutionId !== "string" && obj.selectedExecutionId !== null)
        return false;

    // splitState is either null or a { left, right } object with valid pane states.
    if ("splitState" in obj && obj.splitState !== null) {
        if (typeof obj.splitState !== "object") return false;
        const ss = obj.splitState as Record<string, unknown>;
        if (!isValidPaneState(ss.left) || !isValidPaneState(ss.right)) return false;
    }

    return true;
}

function isValidTabName(tab: unknown): boolean {
    if (typeof tab !== "string") return false;
    return VALID_TAB_NAMES.has(tab) || tab.startsWith("claude-agent-") || tab.startsWith("codex-agent-");
}

function isValidPaneState(pane: unknown): boolean {
    if (typeof pane !== "object" || pane === null) return false;
    const p = pane as Record<string, unknown>;
    if (!Array.isArray(p.tabs) || p.tabs.length === 0) return false;
    if (!p.tabs.every(isValidTabName)) return false;
    if (!isValidTabName(p.activeTab)) return false;
    if (!p.tabs.includes(p.activeTab)) return false;
    return true;
}

/**
 * Fall back to the conversation tab when the active dynamic tab loses its
 * prerequisite (e.g. browser port disappears, log view is cleared, file is
 * closed, environment tab is dismissed). Without this, the Radix tab value
 * doesn't match any trigger and the user sees a blank screen.
 *
 * Skips the first render so it doesn't race with async state restoration
 * (e.g. activeLogView restored via useMountEffect).
 */
export function useOrphanedTabFallback(
    tabName: TabName,
    setTabName: (tab: TabName) => void,
    prerequisites: {
        hasBrowserPort: boolean;
        activeLogView: unknown;
        selectedFilePath: string | null | undefined;
        isEnvironmentTabOpen: boolean;
    },
): void {
    const mounted = useRef(false);
    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        const isOrphanedTab =
            (tabName === "browser" && !prerequisites.hasBrowserPort) ||
            (tabName === "logs" && !prerequisites.activeLogView) ||
            (tabName === "file" && !prerequisites.selectedFilePath) ||
            (tabName === "environment" && !prerequisites.isEnvironmentTabOpen);
        if (isOrphanedTab) {
            setTabName("conversation");
        }
    }, [
        tabName,
        setTabName,
        prerequisites.hasBrowserPort,
        prerequisites.activeLogView,
        prerequisites.selectedFilePath,
        prerequisites.isEnvironmentTabOpen,
    ]);
}

/**
 * Hook that debounces partial writes to per-environment tab state in sessionStorage.
 *
 * Returns `persist(partial)` — call it whenever a subset of tab state changes.
 * Writes are debounced (300ms) and flushed on unmount.
 *
 * Also returns `initialState` — the persisted state read synchronously on first render.
 */
export function useEnvironmentTabState(environmentId: string) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingRef = useRef<Partial<PersistedTabState> | null>(null);

    // Read initial state once (synchronous — sessionStorage is sync).
    const initialState = useRef<PersistedTabState | null>(readEnvironmentTabState(environmentId)).current;

    const flush = useCallback(() => {
        if (pendingRef.current) {
            mergeEnvironmentTabState(environmentId, pendingRef.current);
            pendingRef.current = null;
        }
    }, [environmentId]);

    const persist = useCallback(
        (partial: Partial<PersistedTabState>) => {
            pendingRef.current = { ...(pendingRef.current ?? {}), ...partial };
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                flush();
                timerRef.current = null;
            }, 300);
        },
        [flush],
    );

    // Flush pending writes on unmount so state isn't lost when navigating away.
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            flush();
        };
    }, [flush]);

    return { initialState, persist };
}
